import {
  alpha,
  Box,
  Button,
  GlobalStyles,
  Stack,
  Typography,
} from "@mui/material";
import type { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, type SupportedLanguage } from "@pimo/pimo-l10n";

export type NotFoundPageEvent = "not-found:back";

export type NotFoundPageProps = {
  language: SupportedLanguage;
};

const buttonSx = {
  textTransform: "none",
  borderRadius: "4px",
  padding: "7px 16px",
  whiteSpace: "nowrap",
  minWidth: "fit-content",
} as const;

export const NotFoundPage: PimoReactComponent<
  NotFoundPageProps,
  NotFoundPageEvent
> = ({ fireEvent, language }) => (
  <>
    <GlobalStyles styles={{ body: { margin: 0 } }} />
    <Box
      component="main"
      sx={{
        alignItems: "center",
        boxSizing: "border-box",
        background: (theme) =>
          `radial-gradient(ellipse at 50% 0%, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 60%), #fbfbfd`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        minHeight: "100vh",
        px: 3,
        py: 8,
      }}
    >
      <Box
        component="img"
        src="/pimo-large.svg"
        alt="Pimo"
        sx={{ height: 30, left: 32, position: "absolute", top: 28 }}
      />
      <Stack
        alignItems="center"
        spacing={2}
        sx={{
          "@keyframes not-found-rise": {
            from: { opacity: 0, transform: "translateY(12px)" },
            to: { opacity: 1, transform: "none" },
          },
          "@media (prefers-reduced-motion: no-preference)": {
            animation: "not-found-rise 600ms cubic-bezier(0.2, 0.8, 0.2, 1)",
          },
          maxWidth: 520,
        }}
      >
        <Typography
          component="p"
          aria-hidden="true"
          sx={{
            background: (theme) =>
              `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.35)} 100%)`,
            backgroundClip: "text",
            color: "transparent",
            fontSize: { xs: 120, sm: 176 },
            fontWeight: 300,
            letterSpacing: "-0.06em",
            lineHeight: 1,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          404
        </Typography>
        <Typography
          component="h1"
          sx={{
            color: "primary.main",
            fontSize: { xs: 28, sm: 34 },
            fontWeight: 600,
            letterSpacing: "-0.02em",
            lineHeight: 1.15,
            textAlign: "center",
          }}
        >
          {getLocalizedMessage(language, "notFound.title")}
        </Typography>
        <Typography
          sx={{
            color: "#4a5568",
            fontSize: 17,
            lineHeight: 1.5,
            textAlign: "center",
          }}
        >
          {getLocalizedMessage(language, "notFound.description")}
        </Typography>
        <Typography
          component="div"
          sx={{
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
            borderRadius: 999,
            color: "primary.main",
            fontSize: 13,
            maxWidth: "100%",
            overflowWrap: "anywhere",
            px: 1.75,
            py: 0.5,
            textAlign: "center",
          }}
        >
          {getLocalizedMessage(language, "notFound.requestedAddress")}:{" "}
          <code>{window.location.pathname}</code>
        </Typography>
        <Stack
          alignItems="center"
          direction="row"
          flexWrap="wrap"
          gap={1.5}
          justifyContent="center"
          pt={2}
        >
          <Button
            variant="contained"
            sx={{ ...buttonSx, color: "#fff" }}
            onClick={() => fireEvent?.("not-found:back")}
          >
            {getLocalizedMessage(language, "notFound.back")}
          </Button>
        </Stack>
      </Stack>
    </Box>
  </>
);
